import { useState, useEffect, useCallback } from "react";
import { useAppTheme, useIsMobile } from "./ThemeContext";
import { useAuth } from "./AuthContext";
import { PALETTE, num, fmt, str, Seg, Pill, EditButton, Cell, Section } from "./pmUi";
import PmOffer from "./PmOffer";
import SiteSetupModal from "./SiteSetupModal";

// Projektijuhi vaade (beta): planned quantities and prices against what has actually been hauled and worked.
// Built from the "Projektijuhi vaade (desktop)" mockup; the colours below are the mockup's.
const API = "/api";
const SITE_KEY = "pm_view_site";

const PERIODS = [
  { key: "today", label: "Täna" },
  { key: "yesterday", label: "Eile" },
  { key: "week", label: "Nädal" },
  { key: "month", label: "Kuu" },
];

// Plan rows as edited: numbers stay strings until saved, so half-typed values like "1," aren't lost.
function toDraft(materials) {
  return materials.map((m, i) => ({
    key: m.id ? `id${m.id}` : `src${i}`, id: m.id, name: m.name, sources: m.sources,
    expected: str(m.expected_m3), density: str(m.density), price: str(m.price_t),
    hauled_t: m.hauled_t, period_t: m.period_t,
  }));
}

function matCalc(m) {
  const density = num(m.density);
  const expM3 = num(m.expected);
  const price = num(m.price);
  const expT = expM3 !== null && density ? expM3 * density : null;
  const hauledM3 = density ? m.hauled_t / density : null;
  const leftM3 = expT !== null ? Math.max(expT - m.hauled_t, 0) / density : null;
  const pct = expT ? Math.min((m.hauled_t / expT) * 100, 100) : 0;
  return { density, expM3, price, expT, hauledM3, leftM3, pct, budget: expT !== null && price !== null ? expT * price : null,
           periodCost: price !== null ? m.period_t * price : null };
}

export default function PmView() {
  const { dark } = useAppTheme();
  const { user } = useAuth();
  const mobile = useIsMobile();
  const c = dark ? PALETTE.dark : PALETTE.light;

  const [sites, setSites] = useState([]);
  const [site, setSite] = useState(() => { try { return localStorage.getItem(SITE_KEY) || ""; } catch { return ""; } });
  const [period, setPeriod] = useState("today");
  const [day, setDay] = useState(() => new Date().toLocaleDateString("sv-SE"));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState({ mat: true, mach: true });
  const [tab, setTab] = useState("overview");

  const [matEdit, setMatEdit] = useState(false);
  const [machEdit, setMachEdit] = useState(false);
  const [mats, setMats] = useState([]);
  const [rates, setRates] = useState({});
  const [saving, setSaving] = useState("");
  const [setup, setSetup] = useState(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (sites.length && !sites.includes(site)) setSite(sites[0]);
  }, [sites, site]);

  const loadSites = useCallback(() => fetch(`${API}/sites`, { credentials: "same-origin" })
    .then(r => (r.ok ? r.json() : []))
    .then(list => setSites(list.filter(s => s.active !== false).map(s => s.name)))
    .catch(() => {}), []);

  useEffect(() => { loadSites(); }, [loadSites]);

  const setupDone = ({ name, created, uploaded, uploadError, backfill }) => {
    setSetup(null);
    loadSites().then(() => setSite(name));
    if (created) setTab("offer");
    setNotice([
      created ? `Objekt ${name} loodud.` : `Objekti ${name} seaded salvestatud.`,
      uploaded && "Pakkumine loetud.",
      uploadError && `Pakkumist ei saanud lugeda: ${uploadError}. Proovi uuesti nupust „Lae pakkumine”.`,
      backfill && "Uute allikanimede veod tuuakse järele mõne minuti jooksul.",
    ].filter(Boolean).join(" "));
  };

  useEffect(() => { try { if (site) localStorage.setItem(SITE_KEY, site); } catch {} }, [site]);

  const load = useCallback(() => {
    if (!site) return;
    const q = new URLSearchParams({ site, period, day });
    fetch(`${API}/pm/overview?${q}`, { credentials: "same-origin" })
      .then(async r => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Viga ${r.status}`);
        return r.json();
      })
      .then(d => { setData(d); setError(""); })
      .catch(e => setError(e.message));
  }, [site, period, day]);

  useEffect(() => { load(); }, [load]);

  // Fresh data replaces the tables only while nobody is editing them.
  useEffect(() => { if (data && !matEdit) setMats(toDraft(data.materials)); }, [data, matEdit]);
  useEffect(() => {
    if (data && !machEdit) setRates(Object.fromEntries(data.machines.map(m => [m.name, str(m.rate)])));
  }, [data, machEdit]);

  if (user && user.role !== "admin") {
    return <div style={{ padding: 40, textAlign: "center", color: c.muted }}>See vaade on praegu ainult administraatoritele.</div>;
  }

  const setMat = (key, field, val) => setMats(ms => ms.map(m => (m.key === key ? { ...m, [field]: val } : m)));
  const mergeInto = (fromKey, toKey) => setMats(ms => {
    const from = ms.find(m => m.key === fromKey);
    return ms.filter(m => m.key !== fromKey).map(m => (m.key === toKey ? {
      ...m, sources: [...m.sources, ...from.sources], hauled_t: m.hauled_t + from.hauled_t, period_t: m.period_t + from.period_t,
    } : m));
  });
  const removeRow = key => setMats(ms => ms.filter(m => m.key !== key));
  const addRow = () => setMats(ms => [...ms, {
    key: `new${Date.now()}`, id: null, name: "", sources: [], expected: "", density: "1.60", price: "", hauled_t: 0, period_t: 0, isNew: true,
  }]);

  const post = async (path, body) => {
    const r = await fetch(`${API}${path}?site=${encodeURIComponent(site)}`, {
      method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Viga ${r.status}`);
  };

  const toggleMat = async () => {
    if (!matEdit) { setMatEdit(true); return; }
    // Unplanned rows nobody touched stay unplanned; everything else becomes a plan row.
    const rows = mats
      .filter(m => m.id || m.isNew || m.sources.length > 1 || m.expected || m.price ||
        (data.materials.find(x => !x.id && x.sources[0] === m.sources[0]) || {}).name !== m.name)
      .filter(m => !(m.isNew && !m.name.trim()))
      .map(m => ({ id: m.id, name: m.name.trim() || m.sources[0], sources: m.sources,
                   expected_m3: num(m.expected), density: num(m.density), price_t: num(m.price) }));
    setSaving("mat");
    try {
      await post("/pm/materials", { rows });
      setMatEdit(false);
      load();
    } catch (e) { setError(e.message); }
    setSaving("");
  };

  const toggleMach = async () => {
    if (!machEdit) { setMachEdit(true); return; }
    setSaving("mach");
    try {
      await post("/pm/machines", { rates: Object.entries(rates).map(([name, rate]) => ({ name, rate: num(rate) })) });
      setMachEdit(false);
      load();
    } catch (e) { setError(e.message); }
    setSaving("");
  };

  // ─── figures ───
  const rows = mats.map(m => ({ ...m, calc: matCalc(m) }));
  const machines = (data?.machines || [])
    .filter(m => machEdit || m.hours > 0)
    .map(m => {
      const rate = num(rates[m.name]);
      return { ...m, rateNum: rate, cost: rate !== null ? rate * m.hours : null };
    });
  const mHours = machines.reduce((s, m) => s + m.hours, 0);
  const mCost = machines.reduce((s, m) => s + (m.cost || 0), 0);
  const matPeriodCost = rows.reduce((s, m) => s + (m.calc.periodCost || 0), 0);
  const total = mCost + matPeriodCost;
  const machPct = total > 0 ? Math.round((mCost / total) * 100) : 0;
  const matPct = total > 0 ? 100 - machPct : 0;
  const planned = rows.filter(m => m.calc.expT !== null);
  const leftM3 = planned.reduce((s, m) => s + m.calc.leftM3, 0);
  const expT = planned.reduce((s, m) => s + m.calc.expT, 0);
  const hauledPlannedT = planned.reduce((s, m) => s + m.hauled_t, 0);
  const donePct = expT > 0 ? Math.round(Math.min(hauledPlannedT / expT, 1) * 100) + "%" : "—";
  const unpricedMach = machines.filter(m => m.hours > 0 && m.rateNum === null).length;
  const unpricedMat = rows.filter(m => m.period_t > 0 && m.calc.price === null).length;
  const budget = rows.reduce((s, m) => s + (m.calc.budget || 0), 0);
  const tExpM3 = planned.reduce((s, m) => s + m.calc.expM3, 0);
  const tHauledM3 = rows.reduce((s, m) => s + (m.calc.hauledM3 || 0), 0);

  const periodLabel = PERIODS.find(p => p.key === period)?.label || "";
  const daily = period === "today" || period === "yesterday";
  const matGrid = matEdit
    ? "minmax(220px, 1fr) 128px 112px 112px 96px 108px 120px"
    : "minmax(180px, 1fr) 128px 112px 112px 120px";
  const machGrid = "minmax(160px, 1fr) 140px 130px 120px 130px";
  const tableMin = matEdit ? 960 : 700;
  const headRow = { display: "grid", columnGap: 12, alignItems: "end", padding: "10px 22px", background: c.head,
    borderTop: `1px solid ${c.rowBorder}`, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted };
  const bodyRow = { display: "grid", columnGap: 12, alignItems: "center", padding: "10px 22px", borderTop: `1px solid ${c.rowBorder}`, boxSizing: "border-box" };
  const footRow = { ...bodyRow, padding: "14px 22px", background: c.head, fontSize: 14, fontWeight: 700 };
  const card = { background: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: mobile ? 12 : 14, boxShadow: c.shadow,
    padding: mobile ? "18px 16px" : "26px 24px", minWidth: 0 };
  const selectStyle = { height: 34, padding: "0 10px", fontSize: mobile ? 12 : 13, fontWeight: 500, color: c.text, background: c.ctl,
    border: `1px solid ${c.ctlBorder}`, borderRadius: 8, fontFamily: "inherit", colorScheme: dark ? "dark" : "light" };
  const locked = matEdit || machEdit;

  return (
    <div style={{ background: c.bg, color: c.text, fontVariantNumeric: "tabular-nums" }}>
      <main style={{ maxWidth: 1100, margin: "0 auto", boxSizing: "border-box", padding: mobile ? "16px 12px 90px" : "24px 20px 40px",
        display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: mobile ? 18 : 22, fontWeight: 800 }}>Projektijuhi vaade</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", padding: "3px 8px", borderRadius: 22,
                color: c.accent, background: c.pill }}>BETA</span>
            </div>
            <div style={{ fontSize: mobile ? 11 : 12, color: c.muted, marginTop: 2 }}>
              {site || "Objekt valimata"}{tab === "overview" ? ` · ${periodLabel.toLowerCase()}` : " · pakkumine"}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {site && <button type="button" onClick={() => setSetup({ site })} disabled={locked} aria-label="Objekti seaded"
              title="Objekti seaded: andmeallikad ja ligipääs" style={{ ...selectStyle, height: 38, width: 38, padding: 0, cursor: "pointer", fontSize: 16,
                color: c.muted, background: "transparent", opacity: locked ? 0.5 : 1 }}>⚙</button>}
            <button type="button" onClick={() => setSetup({})} disabled={locked}
              title={locked ? "Salvesta enne muudatused" : "Uus objekt uue pakkumise jaoks"}
              style={{ height: 38, padding: mobile ? "0 14px" : "0 18px", fontSize: mobile ? 12 : 13, fontWeight: 700, borderRadius: 8, border: "none",
                cursor: "pointer", color: c.onAccent, background: c.accent, fontFamily: "inherit", opacity: locked ? 0.5 : 1 }}>
              + Uus objekt
            </button>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Seg c={c} value={tab} onChange={k => { if (locked) return; setTab(k); if (k === "overview") load(); }} items={[
            { key: "overview", label: "Ülevaade" },
            { key: "offer", label: "Pakkumine", disabled: locked, title: locked ? "Salvesta enne muudatused" : undefined },
          ]} />
          <span style={{ width: 1, alignSelf: "stretch", margin: "4px 4px", background: c.cardBorder }} />
          {tab === "overview" && <Seg c={c} items={PERIODS} value={period} onChange={setPeriod} />}
          <div style={{ flexGrow: 1 }} />
          {sites.length > 1 && (
            <select value={site} onChange={e => setSite(e.target.value)} aria-label="Objekt" style={selectStyle} disabled={locked}>
              {sites.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {tab === "overview" && <input type="date" value={day} max={new Date().toLocaleDateString("sv-SE")} onChange={e => e.target.value && setDay(e.target.value)}
            aria-label="Kuupäev" style={selectStyle} />}
        </div>

        {setup && <SiteSetupModal c={c} mobile={mobile} site={setup.site} onClose={() => setSetup(null)} onDone={setupDone} />}
        {notice && (
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: c.pill, color: c.text, padding: "10px 14px", borderRadius: 10, fontSize: 13 }}>
            <span style={{ flex: 1 }}>{notice}</span>
            <button type="button" onClick={() => setNotice("")} aria-label="Sulge" style={{ background: "none", border: "none", color: c.muted, cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
        )}
        {tab === "offer" && site && <PmOffer key={site} site={site} c={c} mobile={mobile} dark={dark} />}
        {tab === "overview" && error && (
          <div style={{ background: c.errorSoft, color: c.error, padding: "10px 14px", borderRadius: 10, fontSize: 13 }}>
            {error}
          </div>
        )}
        {tab === "overview" && !data && !error && <div style={{ padding: 40, textAlign: "center", color: c.muted }}>Laadin…</div>}

        {tab === "overview" && data && (<>
          <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 16, alignItems: "start" }}>
            <section style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted }}>
                  {daily ? "PÄEVAKULU" : `KULU · ${periodLabel.toUpperCase()}`}
                </span>
                <Pill c={c}>{fmt(mHours, 1)} h</Pill>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 14 }}>
                <span style={{ fontSize: mobile ? 32 : 42, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1 }}>{fmt(total)}</span>
                <span style={{ fontSize: mobile ? 14 : 16, fontWeight: 600, color: c.mutedLight }}>€</span>
              </div>
              <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", background: c.track, margin: "16px 0 14px" }}>
                <div style={{ height: "100%", background: c.machBar, width: `${machPct}%` }} />
                <div style={{ height: "100%", background: c.accent, width: `${matPct}%` }} />
              </div>
              {[["Masinad ja autod", c.machBar, machPct, mCost, unpricedMach], ["Materjalid", c.accent, matPct, matPeriodCost, unpricedMat]]
                .map(([label, color, pct, cost, missing], i) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, padding: i ? "10px 0 0" : "10px 0",
                    borderTop: `1px solid ${c.rowBorder}`, fontSize: 13 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600 }}>{label}</span>
                    <span style={{ flexGrow: 1, fontSize: 11, color: c.muted }}>
                      {pct}%{missing > 0 && <span style={{ color: c.warn }}> · {missing} hinnata</span>}
                    </span>
                    <span style={{ fontWeight: 700 }}>{fmt(cost)} €</span>
                  </div>
                ))}
            </section>

            <section style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted }}>JÄÄK VEETA</span>
                <Pill c={c}>{donePct} veetud</Pill>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "14px 0 16px" }}>
                <span style={{ fontSize: mobile ? 32 : 42, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1 }}>{planned.length ? fmt(leftM3) : "—"}</span>
                <span style={{ fontSize: mobile ? 14 : 16, fontWeight: 600, color: c.mutedLight }}>m³</span>
              </div>
              {planned.length === 0 && (
                <div style={{ fontSize: 13, color: c.muted, borderTop: `1px solid ${c.rowBorder}`, padding: "10px 0 0" }}>
                  Sisesta materjalide tabelis oodatavad kogused ja tihedused.
                </div>
              )}
              {planned.map(m => (
                <div key={m.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderTop: `1px solid ${c.rowBorder}`, fontSize: 13 }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke={c.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 1.5l3.5 3.5L3 8.5" /></svg>
                  <span style={{ fontWeight: 600 }}>{m.name || "Uus materjal"}</span>
                  <span style={{ flexGrow: 1 }} />
                  <span style={{ fontWeight: 700 }}>{fmt(m.calc.leftM3)} m³</span>
                </div>
              ))}
            </section>
          </div>

          <Section c={c} title="MATERJALID" open={open.mat} onToggle={() => setOpen(o => ({ ...o, mat: !o.mat }))}
            actions={<EditButton c={c} editing={matEdit} saving={saving === "mat"} onClick={toggleMat} />}
            pill={<Pill c={c}>Eelarve {fmt(budget)} €</Pill>}>
            <div style={{ minWidth: tableMin }}>
              <div style={{ ...headRow, gridTemplateColumns: matGrid }}>
                <div>Materjal</div>
                <div style={{ textAlign: "right" }}>Oodatav m³</div>
                <div style={{ textAlign: "right" }}>Veetud m³</div>
                <div style={{ textAlign: "right" }}>Jääk m³</div>
                {matEdit && <div style={{ textAlign: "right" }}>Tihedus t/m³</div>}
                {matEdit && <div style={{ textAlign: "right" }}>Ostuhind €/t</div>}
                <div style={{ textAlign: "right" }}>Eelarve €</div>
              </div>
              {rows.map(m => {
                const k = m.calc;
                const label = m.name || "Uus materjal";
                const others = rows.filter(o => o.key !== m.key);
                return (
                  <div key={m.key} style={{ ...bodyRow, gridTemplateColumns: matGrid, minHeight: 64 }}>
                    <div style={{ minWidth: 0 }}>
                      {matEdit ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <Cell c={c} editing value={m.name} onChange={v => setMat(m.key, "name", v)} align="left"
                              label={`Materjali nimi: ${label}`} placeholder="Materjali nimi" />
                            {(m.id || m.isNew) && (
                              <button onClick={() => removeRow(m.key)} aria-label={`Eemalda ${label}`} title="Eemalda plaanist"
                                style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 8, border: `1px solid ${c.inBorder}`,
                                  background: "transparent", color: c.soft, cursor: "pointer", fontSize: 18 }}>×</button>
                            )}
                          </div>
                          {others.length > 0 && m.sources.length > 0 && (
                            <select value="" onChange={e => e.target.value && mergeInto(m.key, e.target.value)}
                              aria-label={`Liida ${label} teise reaga`}
                              style={{ ...selectStyle, height: 32, fontSize: 12, color: c.soft, background: c.inBg, borderColor: c.inBorder }}>
                              <option value="">Liida reaga…</option>
                              {others.map(o => <option key={o.key} value={o.key}>{o.name || "Uus materjal"}</option>)}
                            </select>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
                      )}
                      {(m.sources.length > 1 || (m.sources[0] && m.sources[0] !== m.name)) && (
                        <div style={{ fontSize: 11, color: c.muted, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={m.sources.join("\n")}>
                          {m.sources.join(" · ")}
                        </div>
                      )}
                      {!m.id && !m.isNew && !matEdit && (
                        <div style={{ fontSize: 11, color: c.muted, marginTop: 2 }}>planeerimata</div>
                      )}
                    </div>
                    <Cell c={c} editing={matEdit} value={matEdit ? m.expected : (k.expM3 !== null ? fmt(k.expM3) : "")}
                      onChange={v => setMat(m.key, "expected", v)} label={`Oodatav kogus m³: ${label}`} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ textAlign: "right", fontSize: 14, fontWeight: 600 }}>
                        {k.hauledM3 !== null ? fmt(k.hauledM3) : <span style={{ color: c.muted }}>{fmt(m.hauled_t)} t</span>}
                      </div>
                      <div style={{ height: 4, borderRadius: 2, background: c.track, overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: 2, background: c.accent, width: `${k.pct.toFixed(1)}%` }} />
                      </div>
                    </div>
                    <div style={{ textAlign: "right", fontSize: 15, fontWeight: 700 }}>{k.leftM3 !== null ? fmt(k.leftM3) : "—"}</div>
                    {matEdit && <Cell c={c} editing value={m.density} onChange={v => setMat(m.key, "density", v)} label={`Tihedus t/m³: ${label}`} />}
                    {matEdit && <Cell c={c} editing value={m.price} onChange={v => setMat(m.key, "price", v)} label={`Ostuhind €/t: ${label}`} />}
                    <div style={{ textAlign: "right", fontSize: 15, fontWeight: 700 }}>{k.budget !== null ? fmt(k.budget) : "—"}</div>
                  </div>
                );
              })}
              <div style={{ ...footRow, gridTemplateColumns: matGrid }}>
                <div>Kokku</div>
                <div style={{ textAlign: "right", paddingRight: 11 }}>{planned.length ? fmt(tExpM3) : "—"}</div>
                <div style={{ textAlign: "right" }}>{fmt(tHauledM3)}</div>
                <div style={{ textAlign: "right" }}>{planned.length ? fmt(leftM3) : "—"}</div>
                {matEdit && <div />}
                {matEdit && <div />}
                <div style={{ textAlign: "right" }}>{fmt(budget)}</div>
              </div>
              {matEdit && (
                <div style={{ padding: "14px 22px", borderTop: `1px solid ${c.rowBorder}`, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <button onClick={addRow} style={{ height: 44, padding: "0 18px", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14,
                    fontWeight: 600, color: c.accent, background: "transparent", border: `1px solid ${c.accent}`, borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M7 2v10M2 7h10" /></svg>
                    <span>Lisa materjal</span>
                  </button>
                  <button onClick={() => setMatEdit(false)} style={{ all: "unset", cursor: "pointer", fontSize: 13, color: c.soft, padding: "10px 6px" }}>Tühista</button>
                </div>
              )}
            </div>
          </Section>

          <Section c={c} title="MASINATUNNID" open={open.mach} onToggle={() => setOpen(o => ({ ...o, mach: !o.mach }))}
            actions={<EditButton c={c} editing={machEdit} saving={saving === "mach"} onClick={toggleMach} />}
            pill={<Pill c={c}>{fmt(mHours, 1)} h · {fmt(mCost)} €</Pill>}>
            <div style={{ minWidth: 700 }}>
              <div style={{ ...headRow, gridTemplateColumns: machGrid }}>
                <div>Masin</div>
                <div>Tüüp</div>
                <div style={{ textAlign: "right" }}>Tunnihind €/h</div>
                <div style={{ textAlign: "right" }}>Tunnid · {periodLabel.toLowerCase()}</div>
                <div style={{ textAlign: "right" }}>Kulu · {periodLabel.toLowerCase()}</div>
              </div>
              {machines.length === 0 && (
                <div style={{ ...bodyRow, display: "block", color: c.muted, fontSize: 13, padding: "16px 22px" }}>
                  {period === "today" ? "Tänaseid masinatunde pole veel (Infrafly sünkroonib kell 20:00)." : "Selles vahemikus masinatunde pole."}
                </div>
              )}
              {machines.map(x => (
                <div key={x.name} style={{ ...bodyRow, gridTemplateColumns: machGrid, padding: "8px 22px", minHeight: 56 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{x.name}</div>
                  <div style={{ fontSize: 13, color: c.soft }}>{x.type || "—"}</div>
                  <Cell c={c} editing={machEdit} value={machEdit ? (rates[x.name] ?? "") : (x.rateNum !== null ? fmt(x.rateNum, x.rateNum % 1 ? 2 : 0) : "")}
                    onChange={v => setRates(r => ({ ...r, [x.name]: v }))} label={`Tunnihind: ${x.name}`} />
                  <div style={{ textAlign: "right", fontSize: 14, fontWeight: 600 }}>{fmt(x.hours, 1)}</div>
                  <div style={{ textAlign: "right", fontSize: 14, fontWeight: 700 }}>{x.cost !== null ? `${fmt(x.cost)} €` : "—"}</div>
                </div>
              ))}
              <div style={{ ...footRow, gridTemplateColumns: machGrid }}>
                <div>Kokku</div>
                <div />
                <div />
                <div style={{ textAlign: "right" }}>{fmt(mHours, 1)}</div>
                <div style={{ textAlign: "right" }}>{fmt(mCost)} €</div>
              </div>
              {machEdit && (
                <div style={{ padding: "10px 22px 14px", borderTop: `1px solid ${c.rowBorder}` }}>
                  <button onClick={() => setMachEdit(false)} style={{ all: "unset", cursor: "pointer", fontSize: 13, color: c.soft, padding: "10px 6px" }}>Tühista</button>
                </div>
              )}
            </div>
          </Section>
        </>)}
      </main>
    </div>
  );
}
